import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { img } from "../lib/api";
import { useSite } from "../lib/store";

const abs = (site, p) => (!p ? "" : p.startsWith("http") ? p : p.startsWith("/") ? `${site.site_url}${p}` : img(p).startsWith("http") ? img(p).replace(/^https?:\/\/[^/]+/, site.site_url) : `${site.site_url}${img(p)}`);

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(data) {
  document.head.querySelectorAll("script[data-seo]").forEach((s) => s.remove());
  (data || []).filter(Boolean).forEach((d) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.dataset.seo = "1";
    s.textContent = JSON.stringify(d);
    document.head.appendChild(s);
  });
}

export const clip = (s, n = 158) => {
  const t = (s || "").replace(/[#*_>\n\r]+/g, " ").replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n - 1).replace(/\s\S*$/, "")}…` : t;
};

export function useSeo({ title, description, image, type = "website", jsonld, noindex = false } = {}) {
  const { site } = useSite();
  const { pathname } = useLocation();
  const key = JSON.stringify([title, description, image, type, jsonld, noindex]);
  useEffect(() => {
    if (!site) return;
    const t = site.texts || {};
    const brand = site.company?.name || "Materiály pro školky";
    const fullTitle = title ? `${title} | ${brand}` : t.seo_title || brand;
    const desc = clip(description || t.seo_description);
    const url = `${site.site_url}${pathname === "/" ? "" : encodeURI(decodeURI(pathname))}`;
    const im = abs(site, image || t.seo_image || "/logo.png");
    document.title = fullTitle;
    setMeta("name", "description", desc);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    setMeta("name", "google-site-verification", t.google_verification);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", im);
    setMeta("property", "og:site_name", brand);
    setMeta("property", "og:locale", "cs_CZ");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", im);
    setLink("canonical", url);
    setJsonLd(typeof jsonld === "function" ? jsonld(site, abs) : jsonld);
  }, [site, pathname, key]); // eslint-disable-line
}

export function Seo(props) {
  useSeo(props);
  return null;
}

export const orgLd = (site, absUrl) => [
  { "@context": "https://schema.org", "@type": "Organization", name: site.company?.name || "Materiály pro školky", url: site.site_url, logo: absUrl(site, "/logo.png"), sameAs: [site.social?.facebook, site.social?.instagram].filter(Boolean) },
  { "@context": "https://schema.org", "@type": "WebSite", name: site.company?.name || "Materiály pro školky", url: site.site_url, inLanguage: "cs-CZ", potentialAction: { "@type": "SearchAction", target: `${site.site_url}/eshop?q={search_term_string}`, "query-input": "required name=search_term_string" } },
];

export const crumbs = (site, items) => ({
  "@context": "https://schema.org", "@type": "BreadcrumbList",
  itemListElement: [["Domů", "/"], ...items].map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${site.site_url}${path === "/" ? "" : path}` })),
});
